import { useState, useRef } from 'react';
import { Link } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Copy, Check, Share2, ExternalLink } from 'lucide-react';
import { Button } from '../ui/Button';
import { useUpdatePublicProfile, useUploadAvatar, useDeleteAvatar } from '../../hooks/useCommunity';
import { useAuth } from '../../hooks/useAuth';
import { useVault } from '../../context/VaultContext';
import { apiClient } from '../../api/client';
import { useToast } from '../ui/Toast';
import { useTranslation } from '../../i18n';
import { ShareCatalogModal } from './ShareCatalogModal';
import styles from './ProfileEditSection.module.css';

/** Resize an image file to fit within maxPx (longest side) and compress as JPEG. */
function resizeAvatar(file: File, maxPx: number, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(img.src);
      let { width, height } = img;
      if (width > maxPx || height > maxPx) {
        const scale = maxPx / Math.max(width, height);
        width = Math.round(width * scale);
        height = Math.round(height * scale);
      }
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) return reject(new Error('Canvas not supported'));
      ctx.drawImage(img, 0, 0, width, height);
      canvas.toBlob(
        (blob) => (blob ? resolve(blob) : reject(new Error('Compression failed'))),
        'image/jpeg',
        quality,
      );
    };
    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = URL.createObjectURL(file);
  });
}

interface CatalogStats {
  totalViews: number;
  viewsThisWeek: number;
  viewsThisMonth: number;
  topReferrers: Array<{ referrer: string; count: number }>;
}

interface Props {
  bio: string;
  location: string;
  profileSlug: string | null;
  avatarUrl: string | null;
  displayName: string;
}

export function ProfileEditSection({ bio: initialBio, location: initialLocation, profileSlug: initialSlug, avatarUrl, displayName }: Props) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { refreshUser } = useAuth();
  const updateMutation = useUpdatePublicProfile();
  const uploadAvatarMutation = useUploadAvatar();
  const deleteAvatarMutation = useDeleteAvatar();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [bio, setBio] = useState(initialBio);
  const [location, setLocation] = useState(initialLocation);
  const [slug, setSlug] = useState(initialSlug ?? '');
  const [copied, setCopied] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);

  const initials = displayName.slice(0, 2).toUpperCase();

  const publicUrl = initialSlug ? `${window.location.origin}/profile/${initialSlug}` : null;

  const { data: catalogStats } = useQuery<CatalogStats>({
    queryKey: ['catalog-stats'],
    queryFn: () => fetch('/api/profile/catalog-stats', { credentials: 'include' }).then((r) => r.json()),
    staleTime: 60_000,
  });

  async function handleSave() {
    try {
      await updateMutation.mutateAsync({
        bio,
        location,
        profileSlug: slug || null,
      });
      await refreshUser();
      toast(t('community.profileUpdated'), 'success');
    } catch (err: any) {
      toast(err?.message || t('community.profileUpdateFailed'), 'error');
    }
  }

  async function handleAvatarUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const resized = await resizeAvatar(file, 512, 0.85);
      await uploadAvatarMutation.mutateAsync(resized);
      await refreshUser();
      toast(t('community.avatarUpdated'), 'success');
    } catch {
      toast(t('community.avatarUpdateFailed'), 'error');
    }
    e.target.value = '';
  }

  async function handleAvatarDelete() {
    try {
      await deleteAvatarMutation.mutateAsync();
      await refreshUser();
      toast(t('community.avatarRemoved'), 'success');
    } catch {
      toast(t('community.avatarRemoveFailed'), 'error');
    }
  }

  function handleCopy() {
    if (!publicUrl) return;
    navigator.clipboard.writeText(publicUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  const hasChanges = bio !== initialBio || location !== initialLocation || (slug || null) !== initialSlug;

  return (
    <div className={styles.profileCard}>
      <div className={styles.profileCardTitle}>{t('community.publicProfile')}</div>

      <div className={styles.avatarCard}>
        <div className={styles.avatarPreview}>
          {avatarUrl ? (
            <img src={avatarUrl} alt={displayName} className={styles.avatarPreviewImage} />
          ) : (
            initials
          )}
        </div>
        <div className={styles.avatarActions}>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={handleAvatarUpload}
            style={{ display: 'none' }}
          />
          <Button size="small" variant="secondary" onClick={() => fileInputRef.current?.click()}>
            {t('community.uploadAvatar')}
          </Button>
          {avatarUrl && (
            <Button size="small" variant="secondary" onClick={handleAvatarDelete}>
              {t('community.removeAvatar')}
            </Button>
          )}
        </div>
      </div>

      <div className={styles.formGroup}>
        <label className={styles.formLabel}>{t('community.bio')}</label>
        <textarea
          className={styles.formTextarea}
          value={bio}
          onChange={(e) => setBio(e.target.value)}
          maxLength={500}
          rows={3}
          placeholder={t('community.bioPlaceholder')}
        />
      </div>

      <div className={styles.formGroup}>
        <label className={styles.formLabel}>{t('community.location')}</label>
        <input
          className={styles.formInput}
          value={location}
          onChange={(e) => setLocation(e.target.value)}
          maxLength={100}
          placeholder={t('community.locationPlaceholder')}
        />
      </div>

      <div className={styles.formGroup}>
        <label className={styles.formLabel}>{t('community.profileSlug')}</label>
        <div className={styles.slugInputWrap}>
          <span className={styles.slugPrefix}>/profile/</span>
          <input
            className={styles.slugInput}
            value={slug}
            onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
            maxLength={100}
            placeholder={t('community.slugPlaceholder')}
          />
        </div>
      </div>

      <div className={styles.formActions}>
        <Button
          size="small"
          disabled={!hasChanges || updateMutation.isPending}
          onClick={handleSave}
        >
          {updateMutation.isPending ? t('settings.saving') : t('settings.saveChanges')}
        </Button>
      </div>

      {/* ── Public Catalog Sharing ── */}
      <div className={styles.sharingDivider} />

      {publicUrl ? (
        <div className={styles.sharingSection}>
          <div className={styles.sharingTitle}>{t('share.publicCatalog')}</div>
          <div className={styles.sharingUrlRow}>
            <code className={styles.sharingUrl}>{publicUrl}</code>
            <Button size="small" onClick={handleCopy}>
              {copied ? <><Check size={12} strokeWidth={2} /> {t('share.copied')}</> : <><Copy size={12} strokeWidth={2} /> {t('share.copyLink')}</>}
            </Button>
          </div>
          <div className={styles.sharingActions}>
            <Button size="small" variant="secondary" onClick={() => setShowShareModal(true)}>
              <Share2 size={12} strokeWidth={2} />
              {t('share.shareCatalog')}
            </Button>
            <Link
              to="/profile/$slug"
              params={{ slug: initialSlug! }}
              className={styles.sharingViewLink}
            >
              <ExternalLink size={12} strokeWidth={2} />
              {t('share.viewProfile')}
            </Link>
          </div>

          {catalogStats && catalogStats.totalViews > 0 && (
            <div className={styles.sharingStats}>
              <span>
                <strong className={styles.sharingStatValue}>{catalogStats.totalViews}</strong> {t('share.totalViews')}
              </span>
              <span>
                <strong className={styles.sharingStatValue}>{catalogStats.viewsThisWeek}</strong> {t('share.thisWeek')}
              </span>
              <span>
                <strong className={styles.sharingStatValue}>{catalogStats.viewsThisMonth}</strong> {t('share.thisMonth')}
              </span>
            </div>
          )}
        </div>
      ) : (
        <div className={styles.sharingSection}>
          <div className={styles.sharingTitle}>{t('share.publicCatalog')}</div>
          <p className={styles.sharingHint}>{t('share.setSlugToShare')}</p>
        </div>
      )}

      {showShareModal && <ShareCatalogModal onClose={() => setShowShareModal(false)} />}

      {/* ── Shared Vaults ── */}
      {publicUrl && <VaultSharingCheckboxes />}
    </div>
  );
}

function VaultSharingCheckboxes() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { vaults, multiVaultEnabled, refreshVaults } = useVault();
  const queryClient = useQueryClient();
  const [updating, setUpdating] = useState<string | null>(null);

  if (!multiVaultEnabled || vaults.length === 0) return null;

  async function handleToggle(vaultId: string, checked: boolean) {
    setUpdating(vaultId);
    try {
      await apiClient.updateVault(vaultId, { isPublic: checked });
      refreshVaults();
      queryClient.invalidateQueries({ queryKey: ['profile'] });
    } catch {
      toast(t('community.profileUpdateFailed'), 'error');
    } finally {
      setUpdating(null);
    }
  }

  return (
    <>
      <div className={styles.sharingDivider} />
      <div className={styles.vaultSharingSection}>
        <div className={styles.vaultSharingTitle}>{t('share.sharedVaults')}</div>
        <p className={styles.vaultSharingHint}>{t('share.sharedVaultsHint')}</p>
        {vaults.map((vault) => (
          <div key={vault.id} className={styles.vaultRow}>
            <label>
              <input
                type="checkbox"
                checked={vault.isPublic}
                disabled={updating === vault.id}
                onChange={(e) => handleToggle(vault.id, e.target.checked)}
              />
              {vault.name}
            </label>
            <span className={styles.vaultTypeTag}>{vault.vaultType}</span>
          </div>
        ))}
      </div>
    </>
  );
}
